import React, { useState } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import Modal from '../../../Components/UI/Modal/Modal';
import { acceptEstimation, rejectEstimation } from '../../../Store/Actions/actionOffer';

const ClientCheckEstimation = ({
  showModal,
  handleClick,
  demand,
  loading,
  acceptFunction,
  rejectFunction,
}) => {
  const history = useHistory();
  const [submitting, setSubmitting] = useState('');
  const estimation = demand?.estimation;
  const assessmentMode = estimation?.assessment_mode || 'visit_required';
  const assessmentLabels = {
    direct_quote: 'Devis possible à distance',
    visit_required: 'Visite nécessaire',
    more_info: 'Informations complémentaires',
  };
  const opened = showModal === 'open';

  const close = () => {
    if (!submitting) handleClick?.();
  };

  const runDecision = async (decision) => {
    if (!demand || submitting) return;
    setSubmitting(decision);
    const success = decision === 'accept'
      ? await acceptFunction(demand)
      : await rejectFunction(demand);
    setSubmitting('');
    if (success) handleClick?.();
  };

  const openArtisan = () => {
    if (!estimation?.artisan_id) return;
    handleClick?.();
    history.push('/artisanboutique', { artisanId: estimation.artisan_id });
  };

  return (
    <Modal opened={opened} close={close}>
      <Header>
        <div>
          <Eyebrow>Proposition reçue</Eyebrow>
          <Title>Examiner la proposition de l’artisan</Title>
        </div>
        <CloseButton type="button" aria-label="Fermer" onClick={close}>×</CloseButton>
      </Header>

      {!estimation ? (
        <EmptyState>Cette estimation n’est plus disponible.</EmptyState>
      ) : (
        <>
          <ArtisanButton type="button" onClick={openArtisan}>
            <Avatar
              src={estimation.artisan_profile_image || '/images/user.svg'}
              alt=""
            />
            <div>
              <strong>
                {[estimation.artisan_firstname, estimation.artisan_lastname]
                  .filter(Boolean)
                  .join(' ') || 'Artisan'}
              </strong>
              <span>Consulter son profil et ses réalisations</span>
            </div>
          </ArtisanButton>

          <Details>
            <Detail>
              <span>Description</span>
              <p>{estimation.estimation_description || 'Aucun commentaire fourni.'}</p>
            </Detail>
            <DetailGrid>
              <Detail>
                <span>Méthode proposée</span>
                <strong>{assessmentLabels[assessmentMode]}</strong>
              </Detail>
              <Detail>
                <span>Téléphone</span>
                <strong>{estimation.artisan_phone_number || 'Non renseigné'}</strong>
              </Detail>
              {assessmentMode === 'visit_required' && (
                <Detail>
                  <span>Date de visite proposée</span>
                  <strong>{estimation.visit_date || 'À convenir avec l’artisan'}</strong>
                </Detail>
              )}
              {assessmentMode === 'more_info' && (
                <Detail>
                  <span>Informations demandées</span>
                  <p>{estimation.additional_info_request || 'L’artisan vous précisera ses questions dans la conversation.'}</p>
                </Detail>
              )}
              {Number(estimation.estimated_budget) > 0 ? (
                <Detail>
                  <span>Budget estimatif</span>
                  <strong>{Number(estimation.estimated_budget).toLocaleString('fr-FR')} €</strong>
                  <p>Ce montant reste indicatif jusqu’au devis définitif.</p>
                </Detail>
              ) : null}
            </DetailGrid>
          </Details>

          <Notice>
            {assessmentMode === 'direct_quote' && 'En acceptant, l’artisan pourra préparer directement le devis détaillé à partir des informations déjà fournies.'}
            {assessmentMode === 'visit_required' && 'En acceptant, vous confirmez le principe de la visite proposée. Après la visite, l’artisan et vous-même confirmerez qu’elle a eu lieu avant la préparation du devis.'}
            {assessmentMode === 'more_info' && 'En acceptant, vous ouvrez un échange avec l’artisan afin de fournir les précisions nécessaires. Une visite ne sera proposée que si elle devient utile.'}
          </Notice>

          <Actions>
            <SecondaryButton
              type="button"
              disabled={loading || Boolean(submitting)}
              onClick={() => runDecision('reject')}
            >
              {submitting === 'reject' ? 'Refus en cours…' : 'Refuser'}
            </SecondaryButton>
            <PrimaryButton
              type="button"
              disabled={loading || Boolean(submitting)}
              onClick={() => runDecision('accept')}
            >
              {submitting === 'accept' ? 'Validation…' : 'Accepter la proposition'}
            </PrimaryButton>
          </Actions>
        </>
      )}
    </Modal>
  );
};

const Header = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;
const Eyebrow = styled.div`
  margin-bottom: 0.35rem;
  color: #a88a5d;
  font-size: 0.78rem;
  font-weight: 900;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;
const Title = styled.h2`margin: 0; color: #111111; font-size: clamp(1.35rem, 4vw, 1.9rem);`;
const CloseButton = styled.button`
  width: 2.5rem; height: 2.5rem; flex: 0 0 auto; border: 0; border-radius: 50%;
  background: #f1ece5; color: #111111; font: inherit; font-size: 1.6rem; cursor: pointer;
`;
const ArtisanButton = styled.button`
  width: 100%; display: flex; align-items: center; gap: 0.9rem; padding: 1rem;
  border: 1px solid #e5e1da; border-radius: 1rem; background: #faf8f4; color: #111111;
  font: inherit; text-align: left; cursor: pointer;
  div { display: grid; gap: 0.2rem; }
  span { color: #726b61; font-size: 0.88rem; }
  &:hover { border-color: #c0a375; background: #fbf7f1; }
`;
const Avatar = styled.img`width: 3.4rem; height: 3.4rem; border-radius: 50%; object-fit: cover; background: #efede8;`;
const Details = styled.div`display: grid; gap: 0.9rem; margin-top: 1.2rem;`;
const DetailGrid = styled.div`display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.9rem; @media (max-width: 600px) { grid-template-columns: 1fr; }`;
const Detail = styled.div`
  padding: 1rem; border-radius: 0.9rem; background: #faf8f4; color: #111111;
  span { display: block; margin-bottom: 0.4rem; color: #726b61; font-size: 0.78rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; }
  p { margin: 0; white-space: pre-wrap; line-height: 1.6; }
`;
const Notice = styled.p`margin: 1.2rem 0 0; padding: 0.9rem 1rem; border-radius: 0.85rem; background: #f4ede1; color: #8a6f48; line-height: 1.55;`;
const Actions = styled.div`display: flex; justify-content: flex-end; gap: 0.75rem; margin-top: 1.5rem; @media (max-width: 520px) { flex-direction: column-reverse; }`;
const BaseButton = styled.button`
  min-height: 2.8rem; padding: 0.75rem 1.15rem; border-radius: 999px; font: inherit;
  font-weight: 800; cursor: pointer; &:disabled { opacity: 0.55; cursor: wait; }
`;
const SecondaryButton = styled(BaseButton)`border: 1px solid #e7dfd2; background: #fff; color: #9a2f29;`;
const PrimaryButton = styled(BaseButton)`border: 1px solid #c0a375; background: #c0a375; color: #111111;`;
const EmptyState = styled.p`margin: 0; padding: 1rem; border-radius: 0.8rem; background: #faf8f4; color: #66615b;`;

const mapStateToProps = ({ demands }) => ({ loading: demands?.loading });
const mapDispatchToProps = (dispatch) => ({
  acceptFunction: (demand) => dispatch(acceptEstimation(demand)),
  rejectFunction: (demand) => dispatch(rejectEstimation(demand)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ClientCheckEstimation);
